/**
 * #801 — the client half of the protected-branch partition, shared by every
 * delete/move surface (drive, workspace explorer, share view, app shell,
 * mobile). ONE reading of a preview (`classifyPartitionPreview`), ONE mapping
 * of a refusal to words (`partitionRefusalKey`), ONE result sentence per op
 * (`deleteResultMessage` / `moveResultMessage`), and ONE gate hook that shows
 * the preview-before-confirm dialog (`usePartitionGate`).
 *
 * A surface fetches the preview its own way (the drive's `filesApi`, the share
 * view's session route), so every function here takes the preview — or a
 * loader for it — rather than fetching.
 */
import { useCallback, useMemo, type ReactNode } from 'react'
import { useConfirm } from '@papan/core-ui/ui'
import { useTranslation } from '@papan/i18n'
import type { PartitionPreview } from '@papan/types'
import { ProtectedBranchesPreview } from './ProtectedBranchesPreview'
import { JobLostError } from '../client/job-tracker.js'

// The dedicated `@papan/files/ui/protected-branches` entry (not the barrel, whose side
// effects — undoHandlers etc. — the share view has no use for) re-exports the preview too.
export { ProtectedBranchesPreview }

// The pure half (no React) — the preview's reading, the refusal keys and the result sentences —
// lives in `./partition-result`, which the agent's `move_file` tool (worker side) shares.
export {
  previewHasProtected, skippedCountOf, classifyPartitionPreview, partitionRefusalKey,
  deleteResultMessage, moveResultMessage,
  type PartitionOp, type PartitionVerdict, type DeleteResultShape, type MoveResultShape,
} from './partition-result'
import { previewHasProtected, classifyPartitionPreview, partitionRefusalKey, type PartitionOp } from './partition-result'

/**
 * A compound mount id (`s_<mount>_<source>`) names content in ANOTHER tenant:
 * the preview's ids and a split's `binnedFileIds` / `moved` are that SOURCE
 * tenant's ids. Such a surface refreshes the mount folder instead of patching
 * by id, and records no split undo (its ids don't resolve here).
 */
export const isMountId = (id: string): boolean => id.startsWith('s_')

/** The gate's answer: stop (refused or cancelled), or go — already confirmed or not. */
export type PartitionGate =
  | { go: false }
  | { go: true; confirmed: false }
  | { go: true; confirmed: true; expectRemovable: string[] }

/** One item a multi-delete acts on, with the previewed set when it was previewed. */
export interface PartitionActItem<I> { item: I; expectRemovable?: string[] }

/**
 * The preview-before-confirm dialog. `gate` loads one preview and:
 *  - `plain` (or a preview that couldn't be loaded for any reason but a #801
 *    refusal): `{ go: true, confirmed: false }` — the caller runs its own flow;
 *  - a refusal/`entityRoot`/`needsManage`/`nothing`: an info modal, `{ go: false }`;
 *  - `split`: the confirm, then `{ go: true, confirmed: true, expectRemovable }`.
 *
 * `gateDeleteMany` previews every folder in parallel and shows ONE confirm with
 * a `perFolderHeading` section per folder that hits something. A folder whose
 * preview answers 403 (not writable) gets its own section and is LEFT OUT of
 * the act, as is one refused whole. The items that go whole are listed under
 * one "Will be deleted" section, and the intro/button count is what actually
 * goes (whole items + each split folder's removable branches). `null` =
 * cancelled; `confirmed: false` =
 * nothing to show, the caller confirms as before.
 */
export function usePartitionGate() {
  const confirm = useConfirm()
  const { t } = useTranslation('files')

  const info = useCallback(async (message: ReactNode, title?: string) => {
    await confirm({ title: title ?? t('protectedBranches.nothingTitle'), message, type: 'info', confirmOnly: true, confirmText: t('close', { ns: 'common' }) })
  }, [confirm, t])

  const gate = useCallback(async (
    item: { name: string }, op: PartitionOp, load: () => Promise<PartitionPreview>,
    opts?: { deleteTitle?: string; ended?: () => boolean },
  ): Promise<PartitionGate> => {
    let preview: PartitionPreview
    try {
      preview = await load()
    } catch (err) {
      const key = partitionRefusalKey(err, op)
      // #1316 review round 2: a pinned batch whose session ended (`opts.ended`) shows nothing — the item isn't done
      if (key) { if (opts?.ended?.()) return { go: false }; await info(<p>{t(key)}</p>); return { go: false } }
      // Advisory: an unreadable preview never blocks — the act answers for itself.
      return { go: true, confirmed: false }
    }
    const verdict = classifyPartitionPreview(preview, op)
    if (verdict === 'plain') return { go: true, confirmed: false }
    // #1316 review round 2: anything past here asks the person — never in the session after the batch's own; skipped
    if (opts?.ended?.()) return { go: false }
    const body = <ProtectedBranchesPreview op={op} preview={preview} />
    if (verdict === 'blocked') { await info(body, t('protectedBranches.blockedTitle')); return { go: false } }
    if (verdict !== 'split') { await info(body); return { go: false } }
    const ok = await confirm({
      title: op === 'delete' ? (opts?.deleteTitle ?? t('driveView.confirm.deleteTitle')) : item.name,
      message: body,
      type: op === 'delete' ? 'danger' : 'warning',
      confirmText: t(op === 'delete' ? 'protectedBranches.confirmDelete' : 'protectedBranches.confirmMove', { count: preview.removable.length }),
    })
    return ok ? { go: true, confirmed: true, expectRemovable: preview.removable.map(r => r.id) } : { go: false }
  }, [confirm, info, t])

  const gateDeleteMany = useCallback(async <I extends { id: string; name: string; isFolder: boolean }>(
    items: I[], load: (id: string) => Promise<PartitionPreview>, intro: (count: number) => string,
  ): Promise<{ confirmed: boolean; act: PartitionActItem<I>[] } | null> => {
    type Answer = { item: I; preview: PartitionPreview | null; refusal: string | null }
    const answers = await Promise.all(items.map(async (item): Promise<Answer> => {
      if (!item.isFolder) return { item, preview: null, refusal: null }
      try { return { item, preview: await load(item.id), refusal: null } }
      catch (err) {
        // 403 = not writable (e.g. a Locked folder): its own section, left out of the act.
        const forbidden = (err as { status?: unknown })?.status === 403
        return { item, preview: null, refusal: forbidden ? 'protectedBranches.reasonSelf' : partitionRefusalKey(err, 'delete') }
      }
    }))
    const sections: ReactNode[] = []
    const act: PartitionActItem<I>[] = []
    // L4 DEF-5: the items that go WHOLE are named too, and the count is what will
    // actually be deleted — each whole item, plus each split folder's removable branches.
    const whole: I[] = []
    let goingCount = 0
    for (const a of answers) {
      const refusal = a.refusal
      if (refusal) {
        sections.push(
          <div key={a.item.id}>
            <h4 className="font-medium mb-1">{t('protectedBranches.perFolderHeading', { name: a.item.name })}</h4>
            <p className="text-sm">{t(refusal)}</p>
          </div>,
        )
        continue
      }
      const verdict = a.preview ? classifyPartitionPreview(a.preview, 'delete') : 'plain'
      if (verdict === 'plain') { act.push({ item: a.item }); whole.push(a.item); goingCount++; continue }
      sections.push(
        <div key={a.item.id}>
          <h4 className="font-medium mb-1">{t('protectedBranches.perFolderHeading', { name: a.item.name })}</h4>
          <ProtectedBranchesPreview op="delete" preview={a.preview!} />
        </div>,
      )
      if (verdict === 'split') {
        act.push({ item: a.item, expectRemovable: a.preview!.removable.map(r => r.id) })
        goingCount += a.preview!.removable.length
      }
    }
    if (sections.length === 0) return { confirmed: false, act }
    if (act.length === 0) {
      await info(<div className="space-y-4 max-h-[60vh] overflow-y-auto">{sections}</div>)
      return null
    }
    const ok = await confirm({
      title: t('driveView.confirm.deleteTitle'),
      message: (
        <div className="space-y-4 max-h-[60vh] overflow-y-auto">
          <p>{intro(goingCount)}</p>
          {whole.length > 0 && (
            <div>
              <h4 className="font-medium mb-1">{t('protectedBranches.deleteHeading')}</h4>
              <ul className="max-h-64 overflow-y-auto text-sm space-y-0.5">
                {whole.map(item => <li key={item.id}>{item.name}</li>)}
              </ul>
            </div>
          )}
          {sections}
        </div>
      ),
      type: 'danger',
      confirmText: t('protectedBranches.confirmDelete', { count: goingCount }),
    })
    return ok ? { confirmed: true, act } : null
  }, [confirm, info, t])

  return useMemo(() => ({ gate, gateDeleteMany, info }), [gate, gateDeleteMany, info])
}

/** What a "Replace" may do with the item already at the destination (#801). */
export type ReplaceClearOutcome =
  | { cleared: true }
  /** Not cleared: nothing was deleted (the pre-check refused), or — a race backstop —
   *  the delete split after all, and `partial` is what it binned (record it for undo). */
  | { cleared: false; partial?: { binId?: string; binIds: string[]; binnedFileIds: string[]; skipped?: PartitionPreview } }

/**
 * Clear the destination for a "Replace" WITHOUT ever leaving a half-replaced folder:
 * a folder is previewed first, and one holding anything the caller can't delete (or
 * an app/project root, or a preview that can't be read) is NOT deleted — the caller
 * aborts that item. The delete itself still checks `skipped`, as a backstop for a
 * branch that became protected between the preview and the act.
 *
 * `api.deleteFile` should follow a big folder's delete JOB to its end (`deleteUntilGone`, #921):
 * an inline delete of a big folder is cut at the edge while the server goes on. A job whose outcome
 * was lost is NOT cleared — the item may still be there, so the caller aborts this item's move.
 */
export async function clearReplaceTarget(
  existing: { id: string; isFolder: boolean },
  api: {
    protectedBranches: (id: string, op: 'delete') => Promise<PartitionPreview>
    deleteFile: (id: string) => Promise<{ binId?: string; binIds: string[]; binnedFileIds: string[]; skipped?: PartitionPreview }>
  },
): Promise<ReplaceClearOutcome> {
  if (existing.isFolder) {
    let p: PartitionPreview
    try { p = await api.protectedBranches(existing.id, 'delete') }
    catch { return { cleared: false } } // can't tell ⇒ never delete half of it
    if (previewHasProtected(p) || p.entityRoot) return { cleared: false }
  }
  let del: Awaited<ReturnType<typeof api.deleteFile>>
  try { del = await api.deleteFile(existing.id) }
  catch (err) {
    if (err instanceof JobLostError) return { cleared: false }
    throw err
  }
  if (del.skipped) return { cleared: false, partial: del }
  return { cleared: true }
}

/** A chain-escape move parked behind its dialog — what the re-call must carry (#801). */
export interface ParkedChainEscapeMove {
  fileId: string
  targetFolderId: string | null
  /** The previewed set the first call was sent with — the dialog can sit open for
   *  minutes, exactly the window `PARTITION_CHANGED` guards. */
  expectRemovable?: string[]
}

/** The update the "Move anyway" / "Share target" re-call sends: the confirmations
 *  plus the SAME previewed set the first call carried. */
export function chainEscapeRecallUpdate(m: ParkedChainEscapeMove) {
  return {
    parentId: m.targetFolderId, confirmedChainEscape: true, confirmedAccessChange: true,
    ...(m.expectRemovable ? { expectRemovable: m.expectRemovable } : {}),
  }
}

/**
 * REPLACE on a MOVE (owner, 2026-10-06): a FILE moved onto a same-named FILE becomes a new VERSION
 * of the existing file — it keeps its id, history, links and grants — instead of binning the
 * existing file and moving the other in. Is this move one of those? A folder on either side has no
 * versions: it keeps the bin-and-move (`clearReplaceTarget`).
 */
export function moveReplacesAsVersion(
  item: { isFolder: boolean; name: string },
  existing: { isFolder: boolean; name: string; fileType?: string | null } | null | undefined,
): boolean {
  // A `.chain` / `.shared` on either side takes part too (owner, 2026-10-06): onto a link, the moved
  // file's bytes are its new METADATA, through the link's own rules (the server re-points or refuses).
  return !!existing && !item.isFolder && !existing.isFolder
}

export type MoveAsVersionOutcome =
  | {
      ok: true
      /** The destination's NATIVE id (what its version restore is authorised on). */
      fileId: string
      previousVersionId: string | null
      /** The version the Replace wrote (the undo restores only while it is still current). */
      versionId: string
      /** A fork out of another organisation's `.shared`: the moved file stays where it was. */
      fork: boolean
      /** The moved file's Bin entry — removed by the SERVER in the same request (null for a fork). */
      deleted: { binId?: string; binIds: string[]; binnedFileIds: string[]; skipped?: PartitionPreview } | null
    }
  | { ok: false; message: string }

/**
 * What a move with Replace does onto `existing` — the ONE decision both the drag and the cut → paste
 * take:
 *  - `versioned`: a file onto a file became a new VERSION of it (`replace-by-move`, one request);
 *  - `binThenMove`: a folder on either side has no versions — bin the conflicting item, then move;
 *  - `plainMove`: nothing to replace.
 * A destination in ANOTHER organisation is Replaced the same way (#982: no cross-org special case —
 * the server versions it in its own org when the person may write there). Any refusal throws.
 */
export async function moveReplaceStep(
  api: Parameters<typeof moveFileAsVersion>[0],
  item: { id: string; name: string; isFolder: boolean },
  existing: { id: string; name: string; isFolder: boolean; fileType?: string | null } | null | undefined,
): Promise<{ kind: 'versioned'; r: Extract<MoveAsVersionOutcome, { ok: true }> } | { kind: 'binThenMove' } | { kind: 'plainMove' }> {
  if (!existing) return { kind: 'plainMove' }
  if (!moveReplacesAsVersion(item, existing)) return { kind: 'binThenMove' }
  const r = await moveFileAsVersion(api, item, existing)
  if (r.ok) return { kind: 'versioned', r }
  throw new Error(r.message)
}

/**
 * The existing items a conflict pre-flight found, by name, as rows a Replace can target: the server's
 * `existingId` (native, or a compound id through a mount) — for a destination no listing in this view
 * holds (another folder, another org's `.shared`).
 */
export function existingFromConflicts(conflicts: Array<{ name: string; isFolder: boolean; isPointer?: boolean; existingId?: string }>): Map<string, { id: string; name: string; isFolder: boolean; fileType: string | null; parentId?: string | null }> {
  const out = new Map<string, { id: string; name: string; isFolder: boolean; fileType: string | null; parentId?: string | null }>()
  for (const c of conflicts) {
    if (c.existingId) out.set(c.name, { id: c.existingId, name: c.name, isFolder: c.isFolder, fileType: c.isPointer ? 'chain' : null })
  }
  return out
}

/**
 * The ONE undo step a move-with-Replace records. Whenever the server binned the moved file (`deleted`),
 * the undo brings it back — with the destination's version pair when there is one (a Replace that
 * wrote a version), or ALONE when there is none (the bytes equalled the destination's: no version, but
 * the moved file still went to the Bin). A fork (nothing binned) undoes only the version.
 */
export function moveAsVersionUndo(
  r: { fileId: string; previousVersionId: string | null; versionId: string; deleted: { binId?: string; binIds: string[]; binnedFileIds: string[]; skipped?: PartitionPreview } | null },
  item: { id: string; name: string },
):
  | { kind: 'version'; fileId: string; previousVersionId: string; versionId: string; restoreSource?: { binId: string; fileId: string } }
  | { kind: 'delete'; deleted: { binId?: string; binIds: string[]; binnedFileIds: string[]; skipped?: PartitionPreview } }
  | null {
  const restoreSource = r.deleted?.binId ? { binId: r.deleted.binId, fileId: item.id } : undefined
  if (r.previousVersionId) return { kind: 'version', fileId: r.fileId, previousVersionId: r.previousVersionId, versionId: r.versionId, ...(restoreSource ? { restoreSource } : {}) }
  if (r.deleted) return { kind: 'delete', deleted: r.deleted }
  return null
}

/**
 * The move as a version — ONE server request (`replace-by-move`): the existing file takes the moved
 * file's content (by id, through the adopt for an app's live database) and the moved file goes to the
 * Bin, together or not at all, bound to the bytes that were moved (a source changed or no longer
 * removable in between refuses with nothing written). A FORK out of another organisation's `.shared`
 * never removes the source. The client deletes nothing itself.
 */
export async function moveFileAsVersion(
  api: {
    replaceByMove: (destFileId: string, sourceId: string) => Promise<{ fileId: string; versionId: string; previousVersionId: string | null; fork: boolean; deleted: { binId?: string; binIds: string[]; binnedFileIds: string[] } | null }>
  },
  item: { id: string; name: string },
  existing: { id: string },
): Promise<MoveAsVersionOutcome> {
  try {
    const r = await api.replaceByMove(existing.id, item.id)
    return { ok: true, fileId: r.fileId, versionId: r.versionId, previousVersionId: r.previousVersionId, fork: r.fork, deleted: r.fork ? null : r.deleted }
  } catch (err) {
    return { ok: false, message: (err as Error)?.message || `"${item.name}" could not replace the existing file` }
  }
}
